import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Select,
  InputNumber,
  Divider,
  Radio,
  DatePicker,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import {
  fetchCounties,
  fetchData,
  fetchLocation,
  fetchSubCounties,
  fetchSubLocation,
  fetchSubsidySource,
} from "../../../features/fetch/fetchSlice";
import { useWatch } from "antd/es/form/Form";
import dayjs from "dayjs";
import { plannedProjStatusData } from "../../../data";
import { extractYear } from "../../../utils";

const { TextArea } = Input;
const PlannedProjectAdd = () => {
  const urlPathway = "api/v2/irrigationPathway/get";
  const urlProgramme = "api/v2/programs/get";
  const urlProjType = "api/v2/projectType/get";
  const urlThreshold = "api/v2/threshold/get";
  const urlAgency = "api/v2/agency/get";
  const urlScheme = "sch/get_schemes";
  const urlWard = "ward/fetch_wards";
  const urlConstituency = "constituency/fetch_constituencys";

  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const { counties, subCounties, locations, subLocations } = useSelector(
    (state) => state.fetch
  );
  const {
    activeObjPlannedProject,
    dataPathway,
    dataProgramme,
    dataProjType,
    dataThreshold,
    dataAgency,
    dataScheme,
    dataConstituency,
    dataWard,
  } = useSelector((state) => ({
    activeObjPlannedProject: state.obj.activeObj["plannedProject"],
    dataPathway: state.fetch.data[urlPathway] || [],
    dataProgramme: state.fetch.data[urlProgramme] || [],
    dataProjType: state.fetch.data[urlProjType] || [],
    dataThreshold: state.fetch.data[urlThreshold] || [],
    dataAgency: state.fetch.data[urlAgency] || [],
    dataScheme: state.fetch.data[urlScheme] || [],
    loading: state.fetch.loading,
    dataWard: state.fetch.data[urlWard] || [],
    dataConstituency: state.fetch.data[urlConstituency] || [],
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  function handleCancel() {
    navigate("/planned-pipeline");
  }

  const plainOptions = ["Yes", "No"];

  const [projectCountyId, setProjectCountyId] = useState(
    activeObjPlannedProject?.projectCountyId ?? ""
  );
  const [projectConstituencyId, setProjectConstituencyId] = useState(
    activeObjPlannedProject?.projectConstituencyId ?? ""
  );
  const [projectSubCountyId, setProjectSubCountyId] = useState(
    activeObjPlannedProject?.projectSubCountyId ?? ""
  );
  const [projectLocationId, setProjectLocationId] = useState(
    activeObjPlannedProject?.projectLocationId ?? ""
  );
  const [projectSubLocationId, setProjectSubLocationId] = useState(
    activeObjPlannedProject?.projectSubLocationId ?? ""
  );
  const [projectWardId, setProjectWardId] = useState(
    activeObjPlannedProject?.projectWardId ?? ""
  );

  const handleProjectCountyChange = async (value) => {
    setProjectCountyId(value);
    setProjectConstituencyId("");
    setProjectSubCountyId("");
    setProjectLocationId("");
    setProjectSubLocationId("");
    setProjectWardId("");

    form.setFieldsValue({
      projectCountyId: value,
      projectConstituencyId: "",
      projectSubCountyId: "",
      projectLocationId: "",
      projectSubLocationId: "",
      projectWardId: "",
    });

    await dispatch(fetchSubCounties({ subCountyId: value }));
    await dispatch(fetchData({ url: urlConstituency, constCounty: value }));
  };

  const handleProjectConstituencyChange = async (value) => {
    setProjectConstituencyId(value);
    setProjectWardId("");

    form.setFieldsValue({
      projectConstituencyId: value,
      projectWardId: "",
    });

    await dispatch(fetchData({ url: urlWard, wdConst: value }));
  };

  const handleProjectSubCountyChange = async (value) => {
    setProjectSubCountyId(value);
    setProjectLocationId("");
    setProjectSubLocationId("");

    form.setFieldsValue({
      projectSubCountyId: value,
      projectLocationId: "",
      projectSubLocationId: "",
    });

    await dispatch(fetchLocation({ locSubId: value }));
  };

  const handleProjectLocationChange = async (value) => {
    setProjectLocationId(value);
    setProjectSubLocationId("");

    form.setFieldsValue({
      projectLocationId: value,
      projectSubLocationId: "",
    });

    await dispatch(fetchSubLocation({ slocLocId: value }));
  };

  const handleProjectSubLocationChange = (value) => {
    setProjectSubLocationId(value);
    form.setFieldsValue({ projectSubLocationId: value });
  };

  const handleProjectWardChange = (value) => {
    setProjectWardId(value);
    form.setFieldsValue({ projectWardId: value });
  };
  const onFinish = async (data) => {
    data.projectStatus = "PLANNED";
    data.projectId = activeObjPlannedProject?.projectId
      ? activeObjPlannedProject?.projectId
      : null;
    data.projectYear = extractYear(data?.projectYear);

    const res = await dispatch(
      save({
        ...data,
        url: "api/v2/main_projects/save",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  const initialFormValues = {
    ...activeObjPlannedProject,
    projectYear: activeObjPlannedProject?.projectYear
      ? dayjs(new Date(activeObjPlannedProject?.projectYear))
      : null,
  };

  useEffect(() => {
    dispatch(fetchCounties());
    dispatch(
      fetchData({
        url: urlPathway,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlProgramme,
        limit: null,
        start: 0,
      })
    );

    dispatch(
      fetchData({
        url: urlProjType,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlThreshold,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlAgency,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlScheme,
        limit: null,
        start: 0,
      })
    );
    dispatch(fetchSubsidySource());
  }, []);

  useEffect(() => {
    if (activeObjPlannedProject?.projectId) {
      dispatch(
        fetchSubCounties({
          subCountyId: activeObjPlannedProject?.projectCountyId,
        })
      );
      dispatch(
        fetchData({
          url: urlConstituency,
          constCounty: activeObjPlannedProject?.projectCountyId,
        })
      );
      dispatch(
        fetchData({
          url: urlWard,
          wdConst: activeObjPlannedProject?.projectConstituencyId,
        })
      );
      dispatch(
        fetchLocation({ locSubId: activeObjPlannedProject?.projectSubCountyId })
      );
      dispatch(
        fetchSubLocation({
          slocLocId: activeObjPlannedProject?.projectLocationId,
        })
      );
    }
  }, [activeObjPlannedProject, form]);

  const projAssoc = useWatch("projectIsSchemeAssociated", form);

  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          { title: "Planned Projects", path: "/planned-pipeline" },
          { title: "Project Add Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="form-add-h mb-[1.25rem]">Plan a New Project</div>
        <Divider />
        <div className="w-full mt-[1.75rem]">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
          >
            <div className="custom-title">Project Details</div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="projectNumber"
                label={"Project Number"}
                rules={[
                  {
                    required: false,
                    message: "Please add project number",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              <Form.Item
                name="projectName"
                label={"Project Name"}
                rules={[
                  {
                    required: true,
                    message: "Please add project name",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>

              <Form.Item
                name="projectScope"
                label={"Project Scope"}
                rules={[
                  {
                    required: true,
                    message: "Please add project scope",
                  },
                ]}
              >
                <TextArea className="text_area" />
              </Form.Item>

              <Form.Item
                name="projectPurpose"
                label={"Project Purpose"}
                rules={[
                  {
                    required: true,
                    message: "Please add project purpose",
                  },
                ]}
              >
                <TextArea className="text_area" />
              </Form.Item>

              <Form.Item
                name="projectPathwayId"
                className=""
                label={"Pathway"}
                rules={[
                  {
                    required: true,
                    message: "Please select pathway",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataPathway?.length > 0 &&
                    dataPathway?.map((item) => ({
                      value: item?.irrigationPathwayId,
                      label: item?.irrigationPathwayName,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="projectProgramId"
                className=""
                label={"Program"}
                rules={[
                  {
                    required: true,
                    message: "Please select program",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataProgramme?.length > 0 &&
                    dataProgramme?.map((item) => ({
                      value: item?.programId,
                      label: item?.programName,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="projectTypeId"
                className=""
                label={"Project Type"}
                rules={[
                  {
                    required: true,
                    message: "Please select project type",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataProjType?.length > 0 &&
                    dataProjType?.map((item) => ({
                      value: item?.projectTypeId,
                      label: item?.projectTypeName,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="projectIsSchemeAssociated"
                className=""
                label={"Associated to Scheme"}
                rules={[
                  {
                    required: true,
                    message: "Please select if associated with a scheme",
                  },
                ]}
              >
                <Radio.Group options={plainOptions} />
              </Form.Item>
              {projAssoc === "Yes" && (
                <Form.Item
                  name="projectSchemeAssociatedId"
                  className=""
                  label={"Select Associated Scheme"}
                  rules={[
                    {
                      required: true,
                      message: "Please select associated scheme",
                    },
                  ]}
                >
                  <Select
                    className=""
                    allowClear
                    style={{
                      width: "100%",
                    }}
                    options={
                      dataScheme?.length > 0 &&
                      dataScheme?.map((item) => ({
                        value: item?.schId,
                        label: item?.schName,
                      }))
                    }
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.label?.toLocaleLowerCase() ?? "").includes(
                        input?.toLocaleLowerCase()
                      )
                    }
                  />
                </Form.Item>
              )}
            </div>

            <div className="custom-title mt-[2.5rem]">Project Cost</div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="projectCost"
                label={"Contract sum/Projected Cost"}
                rules={[
                  {
                    required: true,
                    message: "Please add contract sum/Projected Cost",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="projectStage"
                className=""
                label={"Project Stage"}
                rules={[
                  {
                    required: true,
                    message: "Please select project stage",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    plannedProjStatusData?.length > 0 &&
                    plannedProjStatusData?.map((item) => ({
                      value: item?.value,
                      label: item?.label,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                label="Project Year"
                name="projectYear"
                rules={[
                  {
                    required: true,
                    message: "Project year is required",
                  },
                ]}
              >
                <DatePicker picker="year" className="input" />
              </Form.Item>
              <Form.Item
                name="projectEngineerName"
                className=""
                label={"Individual/Engineer in Charge"}
                rules={[
                  {
                    required: true,
                    message: "Please give individual/Engineer in charge",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              <Form.Item
                name="projectAgencyId"
                className=""
                label={"Implementing Entity/Department"}
                rules={[
                  {
                    required: true,
                    message: "Please select Implementing Entity/Department",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataAgency?.length > 0 &&
                    dataAgency?.map((item) => ({
                      value: item?.agencyId,
                      label: item?.agencyName,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="projectContractor"
                className=""
                label={"Consultant"}
                rules={[
                  {
                    required: false,
                    message: "Please give consultant",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              {activeObjPlannedProject?.projectId && (
                <Form.Item
                  name="projectThresholdName"
                  className=""
                  label={"Threshhold"}
                  rules={[
                    {
                      required: false,
                      message: "Please select threshhold",
                    },
                  ]}
                >
                  <Input disabled className="input" />
                </Form.Item>
              )}

              <Form.Item
                name="projectCountyId"
                label="County"
                rules={[{ required: true, message: "Please select county" }]}
              >
                <Select
                  allowClear
                  options={counties?.map((c) => ({
                    value: c?.countyId,
                    label: c?.countyName,
                  }))}
                  onChange={handleProjectCountyChange}
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase()?.includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              {projectCountyId && (
                <>
                  <Form.Item
                    name="projectConstituencyId"
                    label="Constituency"
                    rules={[
                      { required: true, message: "Please select constituency" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={dataConstituency?.map((c) => ({
                        value: c?.constId,
                        label: c?.constName,
                      }))}
                      onChange={handleProjectConstituencyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    name="projectSubCountyId"
                    label="Subcounty"
                    rules={[
                      { required: true, message: "Please select subcounty" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={subCounties?.map((s) => ({
                        value: s?.subId,
                        label: s?.subCountyName,
                      }))}
                      onChange={handleProjectSubCountyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </>
              )}

              {projectSubCountyId && (
                <Form.Item
                  name="projectLocationId"
                  label="Location"
                  rules={[
                    { required: true, message: "Please select location" },
                  ]}
                >
                  <Select
                    allowClear
                    options={locations?.map((l) => ({
                      value: l?.locId,
                      label: l?.locLocation,
                    }))}
                    onChange={handleProjectLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {projectLocationId && (
                <Form.Item
                  name="projectSubLocationId"
                  label="Sublocation"
                  rules={[
                    { required: true, message: "Please select sublocation" },
                  ]}
                >
                  <Select
                    allowClear
                    options={subLocations?.map((sl) => ({
                      value: sl?.slocId,
                      label: sl?.slocLocation,
                    }))}
                    onChange={handleProjectSubLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {projectConstituencyId && (
                <Form.Item
                  name="projectWardId"
                  label="Ward"
                  rules={[{ required: true, message: "Please select ward" }]}
                >
                  <Select
                    allowClear
                    options={dataWard?.map((w) => ({
                      value: w?.id,
                      label: w?.wdWard,
                    }))}
                    onChange={handleProjectWardChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}
              <Form.Item
                name="projectLatitude"
                label={"Latitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add latitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="projectLongitude"
                label={"Longitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add longitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
            </div>
            <div className="custom-title mt-[2.5rem]">
              Expected Deliverables
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="projectWaterVolume"
                label={"Volume of Water (M³)"}
                rules={[
                  {
                    required: false,
                    message: "Please add volume of water (M³)",
                  },
                ]}
              >
                <InputNumber
                  suffix={
                    <span>
                      M<sup>3</sup>
                    </span>
                  }
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="projectAreaTarget"
                label={"Targeted Area Under Irrigation"}
                rules={[
                  {
                    required: true,
                    message: "Please add targeted area under irrigation",
                  },
                ]}
              >
                <InputNumber
                  suffix="Acre"
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="projectPotentialBeneficiaries"
                label={"Potential Beneficiaries"}
                rules={[
                  {
                    required: true,
                    message: "Please add potential beneficiaries",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="projectRemarks"
                label={"Remarks"}
                rules={[
                  {
                    required: true,
                    message: "Please add remarks",
                  },
                ]}
              >
                <TextArea className="text_area" />
              </Form.Item>
            </div>

            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>

                <div className="w-[150px]">
                  <button key="submit" type="submit" className="cstm-btn-2">
                    {saving ? (
                      <Spin />
                    ) : activeObjPlannedProject?.projectId ? (
                      "Update"
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default PlannedProjectAdd;
